import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  Paper,
  Slider,
  IconButton,
  Card,
  CardContent,
  Divider,
  FormControlLabel,
  Switch,
  Tooltip,
  TextField,
  InputAdornment,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import { 
  AudioWaveform, 
  LanguageSelector, 
  GenderSelector,
  StatusIndicator,
  LoadingIndicator,
  ErrorMessage,
  OrganizationCard,
  IosAudioPlayer
} from './ui';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import StopIcon from '@mui/icons-material/Stop';
import ClearIcon from '@mui/icons-material/Clear';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import SearchIcon from '@mui/icons-material/Search';
import MaleIcon from '@mui/icons-material/Male';
import FemaleIcon from '@mui/icons-material/Female';
import useTheme from '@mui/material/styles/useTheme';
import config from '../config';
import languageData from '../config/languages.json';
import { useCustomers } from '../contexts/CustomersContext';

const StreamingPage = () => {
  const { customerId } = useParams();
  const { getCustomerById, isValidCustomer, customers, setSelectedCustomerId, getCustomerByShortenedId } = useCustomers();
  const navigate = useNavigate();
  
  // Define two modes: search mode and stream mode
  const isStreamMode = !!customerId;
  
  const [isPrivateStream, setIsPrivateStream] = useState(false);
  const [streamOwner, setStreamOwner] = useState(null);
  const [invalidCustomerId, setInvalidCustomerId] = useState(false);
  
  // Search mode state
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredOrganizations, setFilteredOrganizations] = useState([]);
  
  // Stream mode state
  const [isStreaming, setIsStreaming] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [wsState, setWsState] = useState('disconnected');
  const [transcriptions, setTranscriptions] = useState([]);
  const [targetLanguage, setTargetLanguage] = useState('en-US');
  const [voiceGender, setVoiceGender] = useState('MALE'); // Default voice gender
  const [supportedLanguages, setSupportedLanguages] = useState({});
  const [selectedUser, setSelectedUser] = useState('');
  const [queueStatus, setQueueStatus] = useState({ total: 0, pending: 0 });
  const [useFallbackPlayer, setUseFallbackPlayer] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [triggerAudioUnlock, setTriggerAudioUnlock] = useState(false);
  const [displayedTranscript, setDisplayedTranscript] = useState('');
  const [fullTranscript, setFullTranscript] = useState('');
  const [reconnectAttempt, setReconnectAttempt] = useState(0);
  const [ttsAvailable, setTtsAvailable] = useState(true); // Default to true until we check
  const [checkingTts, setCheckingTts] = useState(false);
  const [genderSupported, setGenderSupported] = useState(true);
  const [supportedGenders, setSupportedGenders] = useState(["MALE", "FEMALE"]); // Default to true until we check
  
  const audioContextRef = useRef(null);
  const wsRef = useRef(null);
  const sourceNodeRef = useRef(null);
  const gainNodeRef = useRef(null);
  const pendingChunksRef = useRef({});
  const lastProcessedChunkIdRef = useRef(0);
  const audioQueueRef = useRef([]);
  const audioQueueProcessingRef = useRef(false);
  const isPlayingRef = useRef(false);
  const lastTranscriptionRef = useRef('');
  const reconnectTimeoutRef = useRef(null);
  const reconnectingRef = useRef(false);
  const heartbeatIntervalRef = useRef(null);
  const pongTimeoutRef = useRef(null);
  const lastPongRef = useRef(Date.now());
  const isStreamingRef = useRef(false);
  const transcriptionBoxRef = useRef(null);
  const [autoScroll, setAutoScroll] = useState(true);
  const fallbackAudioRef = useRef(null);
  const shouldReconnectRef = useRef(true); 
  const sessionMetadataRef = useRef({
    targetLanguage: 'en-US',
    voiceGender: 'MALE',
    selectedUser: '',
    isStreaming: false
  });
  const backoffDelayRef = useRef(1000);
  const reconnectMessageRef = useRef('');

  const initAudioContext = () => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      
      // For iOS, we need to resume the context during user interaction
      if (audioContextRef.current.state === 'suspended') {
        console.log('StreamingPage: AudioContext is suspended, attempting to resume...');
        // We'll try to resume it, but this might not work without user interaction
        audioContextRef.current.resume().then(() => {
          console.log('StreamingPage: AudioContext resumed successfully in initAudioContext');
        }).catch(err => {
          console.error('StreamingPage: Failed to resume AudioContext:', err);
        });
      }
    }
    return audioContextRef.current;
  };

  const connectWebSocket = useCallback(() => {
    if (reconnectingRef.current) {
      console.log('Already attempting to reconnect, skipping this attempt');
      return;
    }
    
    // Check if reconnection should happen
    if (!shouldReconnectRef.current) {
      console.log('Reconnection is disabled, skipping this attempt');
      return;
    }

    // Clear any existing reconnect timeout
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    reconnectingRef.current = true;
    setWsState('reconnecting');
    
    // Create a client ID that identifies this as a streamer
    const streamerId = `${selectedUser}-streamer-${Date.now().toString(36)}`;
    console.log(`Connecting to WebSocket as streamer: ${streamerId}`);
    
    try {
      const ws = new WebSocket(config.getWebSocketUrl(streamerId));
      wsRef.current = ws;
      
      // Set a connection timeout
      const connectionTimeoutId = setTimeout(() => {
        console.log('WebSocket connection timeout');
        if (ws.readyState !== WebSocket.OPEN) {
          ws.close(1000, "Connection timeout");
          // Force reconnect since onclose might not be called
          reconnectingRef.current = false;
          if (shouldReconnectRef.current && isStreamingRef.current) {
            scheduleReconnect();
          }
        }
      }, 5000); // 5 second timeout
      
      ws.onopen = () => {
        console.log('WebSocket connection established');
        clearTimeout(connectionTimeoutId);
        setWsState('connected');
        reconnectingRef.current = false;
        setReconnectAttempt(0);
        
        // Reset the backoff delay on successful connection
        backoffDelayRef.current = 1000;
        
        // Restore session metadata if available
        if (sessionMetadataRef.current.targetLanguage) {
          console.log(`Restoring target language: ${sessionMetadataRef.current.targetLanguage}`);
          ws.send(JSON.stringify({
            type: 'set_language',
            language: sessionMetadataRef.current.targetLanguage
          }));
        }
        
        if (sessionMetadataRef.current.selectedUser) {
          console.log(`Restoring selected user: ${sessionMetadataRef.current.selectedUser}`);
          setSelectedUser(sessionMetadataRef.current.selectedUser);
        }
        
        // Start heartbeat
        startHeartbeat();
        
        // Check for any pending audio chunks that need to be processed
        checkPendingChunks();
      };
      
      ws.onmessage = (event) => {
        const message = JSON.parse(event.data);
        
        if (message.type === 'audio') {
          // Verify the message is from our selected organization
          const senderUser = message.sender ? message.sender.split('-')[0] : null;
          const messageUser = message.user || senderUser;
          
          if (messageUser !== selectedUser) {
            console.log(`StreamingPage: Ignoring audio from organization ${messageUser}, we're listening to ${selectedUser}`);
            return;
          }
          
          handleAudioMessage(message);
        } else if (message.type === 'transcription') {
          // Verify the transcription is from our selected organization
          if (message.user && message.user !== selectedUser) {
            console.log(`StreamingPage: Ignoring transcription from organization ${message.user}, we're listening to ${selectedUser}`);
            return;
          }
          console.log('StreamingPage: Received transcription:', message.text);

          // Check for duplicate transcriptions
          if (lastTranscriptionRef.current === message.text) {
            console.log('StreamingPage: Duplicate transcription message, ignoring.');
            return;
          }
          lastTranscriptionRef.current = message.text;

          // Update full transcript
          setFullTranscript(prev => {
            // Determine if an extra space is needed.
            const addSpace = !(prev.endsWith(' ') || message.text.startsWith(' '));
            return prev + (addSpace ? ' ' : '') + message.text;
          });

          // Update transcriptions state
          setTranscriptions(prev => {
            const newTranscriptions = [...prev, {
              id: message.id || Date.now(),
              text: message.text,
              language: message.language || targetLanguage,
              timestamp: Date.now()
            }];
            // Keep only the last 10 transcriptions
            if (newTranscriptions.length > 10) {
              return newTranscriptions.slice(newTranscriptions.length - 10);
            }
            return newTranscriptions;
          });

          // Scroll to bottom of transcription box
          // Auto-scroll will be handled in the useEffect
        } else if (message.type === 'supported_languages') {
          console.log('StreamingPage: Received supported languages:', message.languages);
          setSupportedLanguages(message.languages);
        } else if (message.type === 'pong') {
          console.log('StreamingPage: Received pong');
          
          // Clear pong timeout
          if (pongTimeoutRef.current) {
            clearTimeout(pongTimeoutRef.current);
            pongTimeoutRef.current = null;
          }
          
          // Update last pong timestamp
          lastPongRef.current = Date.now();
          
          // Calculate round-trip time if client_timestamp is available
          if (message.client_timestamp) {
            const rtt = Date.now() - message.client_timestamp;
            console.log(`StreamingPage: WebSocket RTT: ${rtt}ms`);
          }
        } else if (message.type === 'tts_audio') {
          // Verify the TTS audio is from our selected organization
          if (message.user && message.user !== selectedUser) {
            console.log(`StreamingPage: Ignoring TTS audio from organization ${message.user}, we're listening to ${selectedUser}`);
            return;
          }
          
          console.log('StreamingPage: Received TTS audio');
          
          // Create Int16Array from the message data
          const int16Data = new Int16Array(message.data);
          
          // Convert Int16Array to Float32Array for audio playback
          const float32Data = new Float32Array(int16Data.length);
          for (let i = 0; i < int16Data.length; i++) {
            // Normalize int16 to float32 [-1.0, 1.0]
            float32Data[i] = int16Data[i] / 32768.0;
          }
          
          const chunk = {
            id: 'tts-' + Date.now(),
            data: float32Data,
            sampleRate: message.sampleRate || 16000,
            timestamp: Date.now()
          };
          
          // Add to queue instead of immediately playing
          addToAudioQueue(chunk);
        }
      };
      
      ws.onclose = (event) => {
        console.log(`WebSocket connection closed: ${event.code} ${event.reason}`);
        clearTimeout(connectionTimeoutId);
        setWsState('disconnected');
        
        // Clear heartbeat interval
        if (heartbeatIntervalRef.current) {
          clearInterval(heartbeatIntervalRef.current);
          heartbeatIntervalRef.current = null;
        }
        
        // Clear pong timeout
        if (pongTimeoutRef.current) {
          clearTimeout(pongTimeoutRef.current);
          pongTimeoutRef.current = null;
        }
        
        // Only attempt to reconnect if shouldReconnect flag is true and streaming is active
        reconnectingRef.current = false;
        if (shouldReconnectRef.current && isStreamingRef.current) {
          console.log('Unintentional disconnect detected, scheduling reconnection');
          scheduleReconnect();
        } else {
          console.log('Intentional disconnect, not scheduling reconnection');
        }
      };
      
      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        // Force reconnect in case onclose doesn't get called, but only if should reconnect
        reconnectingRef.current = false;
        if (shouldReconnectRef.current && isStreamingRef.current) {
          console.log('WebSocket error, scheduling reconnection since streaming is active');
          scheduleReconnect();
        }
      };
      
      return ws;
    } catch (error) {
      console.error('Error creating WebSocket:', error);
      reconnectingRef.current = false;
      if (shouldReconnectRef.current && isStreamingRef.current) {
        scheduleReconnect();
      }
      return null;
    }
  }, [selectedUser]);

  const scheduleReconnect = useCallback(() => {
    // Check if reconnection is allowed before scheduling
    if (!shouldReconnectRef.current || !isStreamingRef.current) {
      console.log('Reconnection is disabled or streaming is inactive, not scheduling reconnect');
      return;
    }

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    
    // Add jitter to prevent all clients from reconnecting simultaneously
    const jitter = Math.random() * 1000;
    const delay = backoffDelayRef.current + jitter;
    
    console.log(`Scheduling reconnect in ${Math.round(delay / 1000)} seconds`);
    reconnectMessageRef.current = `Reconnecting in ${Math.round(delay / 1000)} seconds...`;
    console.log(`Reconnecting in ${Math.round(delay / 1000)} seconds...`);
    
    reconnectTimeoutRef.current = setTimeout(() => {
      // Check again if reconnection is still allowed before actually connecting
      if (!shouldReconnectRef.current || !isStreamingRef.current) {
        console.log('Reconnection is no longer needed, cancelling reconnect');
        return;
      }

      // Increase backoff for next attempt (capped at MAX_BACKOFF_DELAY)
      backoffDelayRef.current = Math.min(backoffDelayRef.current * 2, 30000);
      
      setReconnectAttempt(prev => prev + 1);
      reconnectingRef.current = false; // Reset the reconnecting flag
      connectWebSocket();
    }, delay);
  }, [connectWebSocket]);

  const addToAudioQueue = (chunk) => {
    audioQueueRef.current.push(chunk);
    updateQueueStatus();
    
    // Always process the queue when a new chunk is added
    if (!audioQueueProcessingRef.current) {
      processAudioQueue();
    }
  };

  const updateQueueStatus = () => {
    setQueueStatus({
      total: audioQueueRef.current.length + (isPlayingRef.current ? 1 : 0),
      pending: audioQueueRef.current.length
    });
  };

  // Function to check if TTS is available for a language
  const checkTtsAvailability = (langCode) => {
    setCheckingTts(true);
    fetch(`${config.API_URL}/api/check-tts/${langCode}`)
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        return response.json();
      })
      .then(data => {
        console.log('StreamingPage: TTS availability check:', data);
        setTtsAvailable(data.has_tts);
      })
      .catch(error => {
        console.error('StreamingPage: Error checking TTS availability:', error);
        // Default to true if there's an error (safer to assume TTS is available)
        setTtsAvailable(true);
      })
      .finally(() => {
        setCheckingTts(false);
      });
  };

  // Function to fetch supported genders for a language
  const fetchSupportedGenders = (langCode) => {
    console.log(`StreamingPage: Fetching supported genders for ${langCode}`);
    
    // Add timestamp to URL to prevent caching issues
    const timestamp = new Date().getTime();
    fetch(`${config.API_URL}/api/get-supported-genders/${langCode}?_t=${timestamp}`)
      .then(response => {
        if (!response.ok) {
          throw new Error(`API returned ${response.status}: ${response.statusText}`);
        }
        return response.json();
      })
      .then(data => {
        console.log('StreamingPage: Supported genders:', data);
        if (data.supported_genders && data.supported_genders.length > 0) {
          const availableGenders = data.supported_genders;
          setSupportedGenders(availableGenders);
          
          // If current gender is not in available genders, auto-select the first available gender
          if (!availableGenders.includes(voiceGender)) {
            const defaultGender = availableGenders[0]; // Take first available gender as default
            console.log(`StreamingPage: Auto-selecting gender ${defaultGender} as it's the only option available`);
            
            // Update UI state
            setVoiceGender(defaultGender);
            
            // Update session metadata
            sessionMetadataRef.current.voiceGender = defaultGender;
            
            // Notify server about the gender change
            if (wsRef.current && wsState === 'connected') {
              wsRef.current.send(JSON.stringify({
                type: 'set_voice_gender',
                gender: defaultGender
              }));
            }
          }
        } else {
          // Handle the case when no genders are returned
          console.warn('StreamingPage: No supported genders returned from API for', langCode);
          
          // Default to FEMALE as fallback since it's more commonly available
          const defaultGenders = ['FEMALE'];
          setSupportedGenders(defaultGenders);
          
          // Force switch to FEMALE
          if (voiceGender !== 'FEMALE') {
            console.log('StreamingPage: No genders returned, defaulting to FEMALE');
            setVoiceGender('FEMALE');
            
            // Update session metadata
            sessionMetadataRef.current.voiceGender = 'FEMALE';
            if (wsRef.current && wsState === 'connected') {
              wsRef.current.send(JSON.stringify({
                type: 'set_voice_gender',
                gender: 'FEMALE'
              }));
            }
          }
        }
      })
      .catch(error => {
        console.error('StreamingPage: Error fetching supported genders:', error);
        
        // On error, keep current gender but ensure it has an option in dropdown
        const safeGenders = ['MALE', 'FEMALE'];
        setSupportedGenders(safeGenders);
      });
  };

  const handleLanguageChange = (event) => {
    const newLang = event.target.value;
    setTargetLanguage(newLang);
    
    // Update session metadata
    sessionMetadataRef.current.targetLanguage = newLang;
    
    // Notify server about the language change
    if (wsRef.current && wsState === 'connected') {
      wsRef.current.send(JSON.stringify({
        type: 'set_language',
        language: newLang
      }));
      console.log('StreamingPage: Language changed to:', newLang);
    }
    
    // IMPORTANT: Start by checking what genders are supported by the API *before* setting any gender
    console.log(`StreamingPage: Resetting gender options for new language ${newLang}`);
    
    // Don't reset gender state immediately - wait until we know what's supported
    // We'll use an immediate API call to avoid UI flicker
    
    // Check if TTS is available for this language
    checkTtsAvailability(newLang);
    
    // Immediately (synchronously) fetch supported genders to avoid UI flicker
    console.log(`StreamingPage: Immediately fetching supported genders for ${newLang}`);
    
    // Add timestamp to prevent caching
    const timestamp = new Date().getTime();
    fetch(`${config.API_URL}/api/get-supported-genders/${newLang}?_t=${timestamp}`)
      .then(response => {
        if (!response.ok) {
          throw new Error(`API returned ${response.status}: ${response.statusText}`);
        }
        return response.json();
      })
      .then(data => {
        if (data.supported_genders && data.supported_genders.length > 0) {
          const availableGenders = data.supported_genders;
          console.log(`StreamingPage: Received genders for ${newLang}:`, availableGenders);
          
          // Update supported genders in state
          setSupportedGenders(availableGenders);
          
          // Choose the appropriate gender based on what's available
          let selectedGender;
          if (availableGenders.includes('MALE')) {
            selectedGender = 'MALE';
          } else {
            selectedGender = availableGenders[0]; // Use first available
          }
          
          console.log(`StreamingPage: Setting voice gender to ${selectedGender} for ${newLang}`);
          
          // Update UI
          setVoiceGender(selectedGender);
          
          // Update session metadata
          sessionMetadataRef.current.voiceGender = selectedGender;
          
          // Notify server
          if (wsRef.current && wsState === 'connected') {
            wsRef.current.send(JSON.stringify({
              type: 'set_voice_gender',
              gender: selectedGender
            }));
          }
        } else {
          // Default to both if no data
          setSupportedGenders(['MALE', 'FEMALE']);
        }
      })
      .catch(error => {
        console.error('StreamingPage: Error fetching supported genders:', error);
        // Default to showing both options in case of error
        setSupportedGenders(['MALE', 'FEMALE']);
        setVoiceGender('MALE');
      });
  };

  const handleGenderChange = (event) => {
    const newGender = event.target.value;
    setVoiceGender(newGender);
    
    // Update session metadata
    sessionMetadataRef.current.voiceGender = newGender;
    
    // Notify the server about the gender change
    if (wsRef.current && wsState === 'connected') {
      wsRef.current.send(JSON.stringify({
        type: 'set_voice_gender',
        gender: newGender
      }));
      console.log('StreamingPage: Voice gender changed to:', newGender);
    }

    // Check if the selected voice gender is supported for this language
    fetch(`${config.API_URL}/api/check-voice-gender/${targetLanguage}/${newGender}`)
      .then(response => {
        if (!response.ok) {
          throw new Error(`API returned ${response.status}: ${response.statusText}`);
        }
        return response.json();
      })
      .then(data => {
        console.log('StreamingPage: Voice gender support check:', data);
        setGenderSupported(data.is_supported);
      })
      .catch(error => {
        console.error('StreamingPage: Error checking voice gender support:', error);
        // Default to true if there's an error (safer to assume gender is supported)
        setGenderSupported(true);
      });
  };

  const processAudioQueue = async () => {
    // If already processing or no items in queue, return
    if (audioQueueProcessingRef.current || audioQueueRef.current.length === 0) {
      return;
    }
    
    audioQueueProcessingRef.current = true;
    
    try {
      while (audioQueueRef.current.length > 0) {
        // Get the next chunk from the queue
        const nextChunk = audioQueueRef.current.shift();
        updateQueueStatus();
        
        // Play the chunk and wait for it to finish
        await playChunk(nextChunk);
      }
    } catch (error) {
      console.error('StreamingPage: Error processing audio queue:', error);
    } finally {
      audioQueueProcessingRef.current = false;
      updateQueueStatus();
    }
  };

  const playChunk = async (chunk) => {
    try {
      // If there's an existing source node playing, stop it
      if (sourceNodeRef.current) {
        sourceNodeRef.current.stop();
        sourceNodeRef.current.disconnect();
        sourceNodeRef.current = null;
      }
      
      setIsPlaying(true);
      
      // Validate chunk data
      if (!chunk || !chunk.data) {
        console.error('StreamingPage: Invalid chunk data received:', chunk);
        setIsPlaying(false);
        return;
      }
      
      // Ensure chunk.data is a valid array or typed array with a valid length
      if ((!Array.isArray(chunk.data) && !(chunk.data instanceof Float32Array)) || chunk.data.length === 0) {
        console.log('StreamingPage: Processing TTS audio data:', 
          Array.isArray(chunk.data) ? 'Array' : 
          (chunk.data instanceof Float32Array ? 'Float32Array' : typeof chunk.data), 
          'length:', chunk.data?.length || 0);
        
        // Don't return here - we'll try to process it anyway
      }
      
      // For iOS devices, try the fallback player if enabled
      if (isIOS && useFallbackPlayer) {
        return playChunkWithFallback(chunk);
      }
      
      const audioContext = initAudioContext();
      
      // Make sure AudioContext is running, especially important for iOS
      if (audioContext.state === 'suspended') {
        try {
          console.log('StreamingPage: Resuming suspended AudioContext before playback');
          await audioContext.resume();
          console.log('StreamingPage: AudioContext resumed successfully before playback');
        } catch (err) {
          console.error('StreamingPage: Failed to resume AudioContext before playback:', err);
          
          // If we're on iOS and failed to resume, switch to fallback player
          if (isIOS) {
            console.log('StreamingPage: Switching to fallback audio player for iOS');
            setUseFallbackPlayer(true);
            return playChunkWithFallback(chunk);
          }
          // Continue anyway, as we might have unlocked it through the IosAudioPlayer
        }
      }
      
      const audioBuffer = audioContext.createBuffer(1, chunk.data.length, chunk.sampleRate || 48000);
      const channelData = audioBuffer.getChannelData(0);
      
      // Copy the audio data to the buffer
      channelData.set(chunk.data);

      // Apply a short fade-in to eliminate clicks (first 50ms)
      // Implementation based on the memory about eliminating clicking artifacts
      const fadeInSamples = Math.min(Math.floor((chunk.sampleRate || 48000) * 0.05), chunk.data.length); // 50ms fade
      
      // Force first 10 samples to zero to eliminate initial pops
      for (let i = 0; i < Math.min(10, chunk.data.length); i++) {
        channelData[i] = 0;
      }
      
      // Apply cubic fade-in curve for smoother amplitude ramping
      for (let i = 10; i < fadeInSamples; i++) {
        // Cubic curve for smoother amplitude ramping
        const factor = Math.pow(i / fadeInSamples, 3);
        channelData[i] *= factor;
      }

      // Apply a short fade-out at the end to eliminate clicks (last 50ms)
      const endIndex = chunk.data.length - 1;
      for (let i = 0; i < fadeInSamples; i++) {
        if (endIndex - i >= 0) {
          // Cubic curve for smoother amplitude ramping
          const factor = Math.pow(i / fadeInSamples, 3);
          channelData[endIndex - i] *= factor;
        }
      }

      // Create and connect the source
      const source = audioContext.createBufferSource();
      source.buffer = audioBuffer;
      
      // Create a gain node for smoother playback
      const gainNode = audioContext.createGain();
      gainNode.gain.value = 1.0;
      
      // Connect source -> gain -> destination
      source.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      // Store the source for cleanup
      sourceNodeRef.current = source;
      gainNodeRef.current = gainNode;
      
      // Play the audio
      return new Promise((resolve) => {
        source.onended = () => {
          sourceNodeRef.current = null;
          gainNodeRef.current = null;
          setIsPlaying(false);
          resolve();
        };
        
        source.start(0);
      });
    } catch (error) {
      console.error('StreamingPage: Error playing chunk:', error);
      setIsPlaying(false);
    }
  };

  const playChunkWithFallback = async (chunk) => {
    try {
      console.log('StreamingPage: Using fallback audio player for iOS');
      
      // Convert Float32Array to WAV format
      const wavBuffer = float32ArrayToWav(chunk.data, chunk.sampleRate || 48000);
      
      // Create blob and URL
      const blob = new Blob([wavBuffer], { type: 'audio/wav' });
      const audioUrl = URL.createObjectURL(blob);
      
      // Create or reuse audio element
      if (!fallbackAudioRef.current) {
        fallbackAudioRef.current = new Audio();
        
        // Set up event listeners
        fallbackAudioRef.current.onended = () => {
          setIsPlaying(false);
          URL.revokeObjectURL(fallbackAudioRef.current.src); // Clean up
        };
        
        fallbackAudioRef.current.onerror = (error) => {
          console.error('StreamingPage: Fallback audio playback error:', error);
          setIsPlaying(false);
          URL.revokeObjectURL(fallbackAudioRef.current.src); // Clean up
        };
      }
      
      // Set source and play
      fallbackAudioRef.current.src = audioUrl;
      
      // Return promise that resolves when audio finishes playing
      return new Promise((resolve, reject) => {
        const onEnd = () => {
          fallbackAudioRef.current.removeEventListener('ended', onEnd);
          fallbackAudioRef.current.removeEventListener('error', onError);
          setIsPlaying(false);
          resolve();
        };
        
        const onError = (error) => {
          fallbackAudioRef.current.removeEventListener('ended', onEnd);
          fallbackAudioRef.current.removeEventListener('error', onError);
          console.error('StreamingPage: Fallback audio playback error:', error);
          setIsPlaying(false);
          reject(error);
        };
        
        fallbackAudioRef.current.addEventListener('ended', onEnd);
        fallbackAudioRef.current.addEventListener('error', onError);
        
        // Play the audio
        fallbackAudioRef.current.play().catch(error => {
          console.error('StreamingPage: Failed to play audio with fallback:', error);
          setIsPlaying(false);
          reject(error);
        });
      });
    } catch (error) {
      console.error('StreamingPage: Error in fallback audio player:', error);
      setIsPlaying(false);
    }
  };
  
  const float32ArrayToWav = (float32Array, sampleRate) => {
    // Create buffer for WAV file
    const buffer = new ArrayBuffer(44 + float32Array.length * 2);
    const view = new DataView(buffer);
    
    // Write WAV header
    // "RIFF" chunk descriptor
    writeString(view, 0, 'RIFF');
    view.setUint32(4, 36 + float32Array.length * 2, true);
    writeString(view, 8, 'WAVE');
    
    // "fmt " sub-chunk
    writeString(view, 12, 'fmt ');
    view.setUint32(16, 16, true); // subchunk1 size
    view.setUint16(20, 1, true); // PCM format
    view.setUint16(22, 1, true); // mono channel
    view.setUint32(24, sampleRate, true); // sample rate
    view.setUint32(28, sampleRate * 2, true); // byte rate
    view.setUint16(32, 2, true); // block align
    view.setUint16(34, 16, true); // bits per sample
    
    // "data" sub-chunk
    writeString(view, 36, 'data');
    view.setUint32(40, float32Array.length * 2, true);
    
    // Write audio data
    const volume = 0.9; // Adjust volume to prevent clipping
    for (let i = 0; i < float32Array.length; i++) {
      // Convert float32 [-1.0, 1.0] to int16 [-32768, 32767]
      const sample = Math.max(-1, Math.min(1, float32Array[i])) * volume;
      const int16Sample = sample < 0 ? sample * 32768 : sample * 32767;
      view.setInt16(44 + i * 2, int16Sample, true);
    }
    
    return buffer;
  };
  
  const writeString = (view, offset, string) => {
    for (let i = 0; i < string.length; i++) {
      view.setUint8(offset + i, string.charCodeAt(i));
    }
  };

  const handleAudioMessage = async (message) => {
    try {
      // Initialize audio context if it doesn't exist
      if (!audioContextRef.current) {
        console.log('StreamingPage: Audio context not initialized, attempting to initialize');
        audioContextRef.current = initAudioContext();
        
        // If still not initialized, log error and return
        if (!audioContextRef.current) {
          console.error('StreamingPage: Failed to initialize audio context');
          return;
        }
      }
      
      // Update received chunks counter
      // Audio chunk received
      
      // Check if the message has a chunkId
      const chunkId = message.chunkId || 0;
      
      // If we're processing chunks in order and this one is not the next in sequence
      if (chunkId > 0 && chunkId !== lastProcessedChunkIdRef.current + 1) {
        // Store it in pending chunks
        pendingChunksRef.current[chunkId] = message;
        console.log(`StreamingPage: Queued out-of-order chunk ${chunkId}, waiting for chunk ${lastProcessedChunkIdRef.current + 1}`);
        return;
      }
      
      // Process this chunk
      await processAudioChunk(message);
    } catch (error) {
      console.error('StreamingPage: Error handling audio message:', error);
    }
  };

  const processAudioChunk = async (message) => {
    try {
      const audioData = message.data;
      const chunkId = message.chunkId || 0;
      
      // Update last processed chunk id
      if (chunkId > 0) {
        lastProcessedChunkIdRef.current = chunkId;
      }
      
      // Update processed chunks counter
      // Process audio chunk
      
      // Check if the data is already in binary format (Int16Array or Float32Array)
      if (Array.isArray(audioData)) {
        // Handle array data (likely already processed)
        const audioContext = audioContextRef.current;
        
        // Convert to Float32Array if needed
        const float32Data = new Float32Array(audioData.length);
        for (let i = 0; i < audioData.length; i++) {
          // Normalize to [-1.0, 1.0] if needed
          float32Data[i] = typeof audioData[i] === 'number' ? 
            (Math.abs(audioData[i]) > 1 ? audioData[i] / 32768.0 : audioData[i]) : 0;
        }
        
        // Create an audio buffer
        const audioBuffer = audioContext.createBuffer(1, float32Data.length, 16000);
        const channelData = audioBuffer.getChannelData(0);
        channelData.set(float32Data);
        
        // Apply fade-in to prevent clicking artifacts
        applyFadeIn(audioBuffer);
        
        // Add to queue
        audioQueueRef.current.push(audioBuffer);
        updateQueueStatus();
        
        // Process the queue
        if (!audioQueueProcessingRef.current) {
          processAudioQueue();
        }
        return;
      }
      
      // Process Base64 encoded data
      if (typeof audioData === 'string') {
        try {
          // Validate Base64 string - it should only contain valid Base64 characters
          if (!/^[A-Za-z0-9+/=]+$/.test(audioData)) {
            console.warn('StreamingPage: Invalid Base64 characters in audio data, skipping chunk');
            return;
          }
          
          // Try to decode the Base64 string
          const binaryString = atob(audioData);
          const bytes = new Uint8Array(binaryString.length);
          
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          
          const audioContext = audioContextRef.current;
          
          audioContext.decodeAudioData(
            bytes.buffer,
            (audioBuffer) => {
              // Apply fade-in to prevent clicking artifacts
              applyFadeIn(audioBuffer);
              
              // Add to queue
              audioQueueRef.current.push(audioBuffer);
              updateQueueStatus();
              
              // Always process the queue when a new buffer is added
              if (!audioQueueProcessingRef.current) {
                processAudioQueue();
              }
            },
            (error) => {
              console.error('StreamingPage: Error decoding audio data:', error);
            }
          );
        } catch (decodeError) {
          console.error('StreamingPage: Error decoding Base64 audio data:', decodeError);
        }
      } else {
        console.warn('StreamingPage: Received audio data in unsupported format, skipping chunk');
      }
    } catch (error) {
      console.error('StreamingPage: Error processing audio chunk:', error);
    }
  };

  const checkPendingChunks = () => {
    const nextChunkId = lastProcessedChunkIdRef.current + 1;
    
    if (pendingChunksRef.current[nextChunkId]) {
      const nextChunk = pendingChunksRef.current[nextChunkId];
      delete pendingChunksRef.current[nextChunkId];
      
      // Process this chunk
      processAudioChunk(nextChunk).then(() => {
        // Recursively check for more pending chunks
        checkPendingChunks();
      });
    }
  };

  const applyFadeIn = (buffer) => {
    // Implementation based on the memory about eliminating clicking artifacts
    const fadeInSamples = 800; // 50ms at 16kHz
    
    // Get the audio data
    const channelData = buffer.getChannelData(0);
    
    // Force first 10 samples to zero to eliminate initial pops
    for (let i = 0; i < 10; i++) {
      channelData[i] = 0;
    }
    
    // Apply cubic fade-in curve for smoother amplitude ramping
    for (let i = 10; i < Math.min(fadeInSamples, channelData.length); i++) {
      const factor = Math.pow(i / fadeInSamples, 3);
      channelData[i] *= factor;
    }
  };

  useEffect(() => {
    if (isStreaming) {
      // Try to initialize audio context when streaming starts
      const context = initAudioContext();
      
      // For iOS devices, we need to handle audio unlocking
      if (context && context.state === 'suspended') {
        console.log('StreamingPage: AudioContext is suspended, waiting for user interaction');
        
        // Create a silent audio buffer and play it to unlock audio context
        const unlockAudio = () => {
          const buffer = context.createBuffer(1, 1, 22050);
          const source = context.createBufferSource();
          source.buffer = buffer;
          source.connect(context.destination);
          source.start(0);
          
          // Remove the event listeners once audio is unlocked
          document.removeEventListener('touchstart', unlockAudio);
          document.removeEventListener('touchend', unlockAudio);
          document.removeEventListener('click', unlockAudio);
          
          console.log('StreamingPage: Audio context unlocked by user interaction');
        };
        
        // Add event listeners to unlock audio on user interaction
        document.addEventListener('touchstart', unlockAudio, false);
        document.addEventListener('touchend', unlockAudio, false);
        document.addEventListener('click', unlockAudio, false);
      }
    }
  }, [isStreaming]);

  useEffect(() => {
    isStreamingRef.current = isStreaming;
    sessionMetadataRef.current.isStreaming = isStreaming;
  }, [isStreaming]);

  useEffect(() => {
    isPlayingRef.current = isPlaying;
    updateQueueStatus();
    
    // If finished playing and there are more items in the queue, process the next one
    if (!isPlaying && audioQueueRef.current.length > 0) {
      processAudioQueue();
    }
  }, [isPlaying]);

  useEffect(() => {
    // Detect iOS devices
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);
    
    // Initialize session metadata
    sessionMetadataRef.current = {
      targetLanguage: 'en-US',
      selectedUser,
      isStreaming: isStreamingRef.current
    };
    
    return () => {
      // Cleanup on component unmount
      shouldReconnectRef.current = false; // Prevent reconnection on unmount
      if (sourceNodeRef.current) {
        sourceNodeRef.current.stop();
        sourceNodeRef.current.disconnect();
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
      if (wsRef.current) {
        wsRef.current.close(1000, "Component unmounting");
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      stopHeartbeat();
    };
  }, []);

  useEffect(() => {
    // Typewriter effect for displaying transcript
    // If the transcript has been shortened, reset immediately.
    if (fullTranscript.length < displayedTranscript.length) {
      setDisplayedTranscript(fullTranscript);
      return;
    }

    // Compute the new text to animate.
    const newText = fullTranscript.substring(displayedTranscript.length);

    if (newText.length === 0) {
      return;
    }

    // Use a local variable to track the updated text.
    let currentText = displayedTranscript;
    let currentIndex = 0;
    const timer = setInterval(() => {
      const charToAppend = newText[currentIndex];
      currentText += charToAppend;
      setDisplayedTranscript(currentText);
      currentIndex++;
      if (currentIndex >= newText.length) {
        clearInterval(timer);
      }
    }, 25); // Adjust speed as needed.

    return () => {
      clearInterval(timer);
    };
  }, [fullTranscript, displayedTranscript]);

  useEffect(() => {
    if (selectedUser) {
      console.log('StreamingPage: Organization changed to:', selectedUser);
      
      // Update session metadata
      sessionMetadataRef.current.selectedUser = selectedUser;
      
      // Set shouldReconnect to false to prevent immediate reconnection when changing organization
      shouldReconnectRef.current = false;
      
      // Close existing connection if any
      if (wsRef.current) {
        console.log('StreamingPage: Closing existing connection');
        wsRef.current.close(1000, "User changed organization");
      }
      
      // Clear any pending reconnection attempts
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      reconnectingRef.current = false;
      
      // Reset state
      setTranscriptions([]);
      setFullTranscript('');
      setDisplayedTranscript('');
      clearAudioQueue();
      setReconnectAttempt(0);
      // Clear reconnect logs
      
      // After closing the previous connection and cleaning up, 
      // enable reconnection but only connect if isStreaming is true
      setTimeout(() => {
        shouldReconnectRef.current = true;
        
        // Only connect to new organization if streaming was active
        if (isStreamingRef.current) {
          console.log('StreamingPage: Reconnecting to new organization since streaming is active');
          connectWebSocket();
        } else {
          console.log('StreamingPage: Not connecting to new organization since streaming is inactive');
        }
      }, 100);
    } else {
      // Set shouldReconnect to false when no organization is selected
      shouldReconnectRef.current = false;
      
      if (wsRef.current) {
        console.log('StreamingPage: Closing WebSocket - no organization selected');
        wsRef.current.close(1000, "No organization selected");
        wsRef.current = null;
      }
      setWsState('disconnected');
    }
  }, [selectedUser, connectWebSocket]);

  // Handle private stream via customer ID in URL
  useEffect(() => {
    if (customerId) {
      // Use the utility function to find the customer by shortened ID
      const customer = getCustomerByShortenedId(customerId);
      
      if (customer) {
        setSelectedUser(customer.id);
        setSelectedCustomerId(customer.id);
        setStreamOwner(customer);
        setIsPrivateStream(true);
        setInvalidCustomerId(false);
        console.log(`StreamingPage: Private stream for customer ID: ${customer.id}`);
      } else {
        console.error(`StreamingPage: Invalid customer ID in URL: ${customerId}`);
        setInvalidCustomerId(true);
        setIsPrivateStream(false);
      }
    }
  }, [customerId, getCustomerByShortenedId, setSelectedCustomerId]);

  const theme = useTheme();
  
  // Function to navigate to an organization's stream page
  const goToOrganizationStream = (orgId) => {
    // Extract the unique part of the customer ID (after "cus_" if it exists)
    const shortId = orgId.includes('cus_') ? orgId.split('cus_')[1] : orgId;
    navigate(`/stream/${shortId}`);
  };

  // Handle organization search in search mode
  useEffect(() => {
    if (isStreamMode) return;
    
    if (searchQuery.trim() === '') {
      // If search query is empty, show no organizations
      setFilteredOrganizations([]);
    } else {
      const query = searchQuery.trim().toLowerCase();
      const filtered = customers.filter(org => 
        org.displayName.toLowerCase().includes(query)
      );
      setFilteredOrganizations(filtered);
    }
  }, [searchQuery, customers, isStreamMode]);
  
  // Handle customer ID and org selection in stream mode
  useEffect(() => {
    if (isStreamMode) {
      // Use the utility function to find the customer by shortened ID
      const customer = getCustomerByShortenedId(customerId);
      
      if (customer) {
        setSelectedUser(customer.id);
        setSelectedCustomerId(customer.id);
        setStreamOwner(customer);
        setIsPrivateStream(true);
        setInvalidCustomerId(false);
        console.log(`StreamingPage: Private stream for customer ID: ${customer.id}`);
      } else {
        console.error(`StreamingPage: Invalid customer ID in URL: ${customerId}`);
        setInvalidCustomerId(true);
        setIsPrivateStream(false);
      }
    }
  }, [customerId, getCustomerByShortenedId, setSelectedCustomerId, isStreamMode]);

  const toggleStreaming = () => {
    if (isStreaming) {
      // Stop streaming
      console.log('StreamingPage: Stopping streaming');
      
      // Set shouldReconnect to false to prevent reconnection
      shouldReconnectRef.current = false;
      
      if (wsRef.current) {
        // Indicate this is a clean closure to prevent reconnection attempts
        wsRef.current.close(1000, "User initiated disconnect");
      }
      setIsStreaming(false);
      isStreamingRef.current = false;
      sessionMetadataRef.current.isStreaming = false;
      
      // Clear any pending reconnection attempts
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      reconnectingRef.current = false;
      
      // Stop heartbeat mechanism
      stopHeartbeat();
    } else {
      // Start streaming
      if (!selectedUser) {
        console.error('StreamingPage: Cannot start streaming - no organization selected');
        return;
      }
      
      // For iOS devices, trigger audio unlocking when starting streaming
      if (isIOS) {
        console.log('StreamingPage: Triggering audio unlock for iOS');
        setTriggerAudioUnlock(true);
        
        // Reset trigger after a short delay
        setTimeout(() => {
          setTriggerAudioUnlock(false);
        }, 1000);
      }
      
      // Enable reconnection when starting streaming
      shouldReconnectRef.current = true;
      
      console.log('StreamingPage: Starting streaming for organization:', selectedUser);
      console.log('StreamingPage: TTS availability:', ttsAvailable ? 'Available' : 'Not available');
      setIsStreaming(true);
      isStreamingRef.current = true;
      sessionMetadataRef.current.isStreaming = true;
      
      if (wsState !== 'connected' && wsState !== 'reconnecting') {
        connectWebSocket();
      }
    }
  };

  const clearAudioQueue = () => {
    audioQueueRef.current = [];
    updateQueueStatus();
  };

  // Function to check if user is at the bottom of the transcription container
  const isAtBottom = () => {
    if (!transcriptionBoxRef.current) return true;
    
    const { scrollTop, scrollHeight, clientHeight } = transcriptionBoxRef.current;
    // Consider "at bottom" if within 20px of the actual bottom
    return scrollHeight - scrollTop - clientHeight < 20;
  };

  // Handle scroll events in the transcription box
  const handleTranscriptionScroll = () => {
    // If user scrolls to bottom, enable auto-scroll
    // If user scrolls away from bottom, disable auto-scroll
    setAutoScroll(isAtBottom());
  };

  // Effect to handle auto-scrolling of transcription box
  useEffect(() => {
    if (autoScroll && transcriptionBoxRef.current && fullTranscript.trim() !== '') {
      // Use smooth scrolling behavior instead of instant jump
      transcriptionBoxRef.current.scrollTo({
        top: transcriptionBoxRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, [fullTranscript, displayedTranscript, autoScroll]);

  const handleAudioUnlocked = (audioContext) => {
    audioContextRef.current = audioContext;
    console.log('StreamingPage: Audio unlocked via IosAudioPlayer');
    
    // Reset fallback player flag when audio is successfully unlocked
    setUseFallbackPlayer(false);
    
    // Process any pending audio in the queue now that audio is unlocked
    if (audioQueueRef.current.length > 0 && !audioQueueProcessingRef.current) {
      console.log('StreamingPage: Processing audio queue after unlock');
      processAudioQueue();
    }
  };

  const startHeartbeat = useCallback(() => {
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
    }
    
    heartbeatIntervalRef.current = setInterval(() => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        const timestamp = new Date().toLocaleTimeString();
        console.log('StreamingPage: Sending ping');
        
        // Set timeout for pong response
        if (pongTimeoutRef.current) {
          clearTimeout(pongTimeoutRef.current);
        }
        
        pongTimeoutRef.current = setTimeout(() => {
          const timeSinceLastPong = Date.now() - lastPongRef.current;
          const failureMessage = `${timestamp}: Ping failed - no pong response received for ${Math.round(timeSinceLastPong/1000)}s`;
          console.error('StreamingPage:', failureMessage);
          
          // Add to ping log
          console.warn(failureMessage);
          
          // Close the connection to trigger reconnect
          if (wsRef.current) {
            console.log('StreamingPage: Closing connection due to ping timeout');
            wsRef.current.close();
          }
        }, 5000);
        
        // Send ping message
        wsRef.current.send(JSON.stringify({
          type: 'ping',
          timestamp: Date.now()
        }));
      }
    }, 10000);
  }, [scheduleReconnect]);

  const stopHeartbeat = () => {
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }
    
    if (pongTimeoutRef.current) {
      clearTimeout(pongTimeoutRef.current);
      pongTimeoutRef.current = null;
    }
  };

  // Generate structured data for SEO (JSON-LD format)
  const generateStructuredData = () => {
    if (isStreamMode && streamOwner) {
      // Organization page structured data
      return {
        "@context": "https://schema.org",
        "@type": "AudioObject",
        "name": `${streamOwner.displayName} Audio Stream`,
        "description": `Listen to ${streamOwner.displayName}'s real-time audio broadcasts with automatic translation in multiple languages.`,
        "encodingFormat": "audio/wav",
        "inLanguage": targetLanguage,
        "potentialAction": {
          "@type": "ListenAction",
          "target": window.location.href
        }
      };
    } else {
      // Search page structured data
      return {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": "Find Organizations to Listen To",
        "description": "Search for organizations to listen to their real-time audio broadcasts with automatic translation.",
        "mainEntity": {
          "@type": "ItemList",
          "itemListElement": filteredOrganizations.map((org, index) => ({
            "@type": "ListItem",
            "position": index + 1,
            "item": {
              "@type": "Organization",
              "name": org.displayName,
              "url": `${window.location.origin}/stream/${org.id.includes('cus_') ? org.id.split('cus_')[1] : org.id}`
            }
          }))
        }
      };
    }
  };

  // Render different content based on mode
  if (!isStreamMode) {
    return (
      <Box 
        component="main" 
        sx={{ py: 2 }}
        role="main"
        aria-label="Organization search page"
      >
        {/* Add JSON-LD structured data for SEO */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(generateStructuredData())
          }}
        />
        
        <Paper 
          elevation={3} 
          sx={{ 
            p: 4, 
            borderRadius: 2,
            maxWidth: '800px',
            mx: 'auto'
          }}
        >
          <Typography 
            variant="h1" 
            component="h1" 
            gutterBottom 
            sx={{ 
              textAlign: 'center', 
              mb: 3, 
              fontSize: '2rem',
              fontWeight: 600
            }}
          >
            Find an Organization to Listen To
          </Typography>
          
          <Typography 
            variant="body1" 
            component="p" 
            sx={{ 
              mb: 3, 
              textAlign: 'center',
              maxWidth: '600px',
              mx: 'auto'
            }}
          >
            Search for an organization by name to listen to their real-time audio broadcasts with automatic translation in your preferred language.
          </Typography>
          
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search organizations by name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ mb: 4 }}
            aria-label="Search for organizations"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          
          {searchQuery.trim() === '' ? (
            <Box sx={{ textAlign: 'center', my: 6, color: 'text.secondary' }}>
              <Typography variant="h6">
                Enter an organization name to begin
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                You'll be able to listen to their broadcasts once you find them
              </Typography>
            </Box>
          ) : filteredOrganizations.length === 0 ? (
            <Box sx={{ textAlign: 'center', my: 6, color: 'text.secondary' }}>
              <Typography variant="h6">
                No organizations found
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Try a different search term
              </Typography>
            </Box>
          ) : (
            <Card elevation={2}>
              <List sx={{ width: '100%', bgcolor: 'background.paper', py: 0 }}>
                {filteredOrganizations.map(org => (
                  <React.Fragment key={org.id}>
                    <ListItem 
                      button
                      onClick={() => goToOrganizationStream(org.id)}
                      sx={{ 
                        py: 1.5,
                        transition: 'all 0.2s',
                        '&:hover': {
                          bgcolor: 'action.hover'
                        }
                      }}
                    >
                      <ListItemText 
                        primary={org.displayName}
                        primaryTypographyProps={{
                          fontWeight: 500,
                          variant: 'subtitle1'
                        }}
                      />
                      <ListItemIcon sx={{ minWidth: 'auto' }}>
                        <PlayCircleOutlineIcon color="primary" />
                      </ListItemIcon>
                    </ListItem>
                    <Divider component="li" />
                  </React.Fragment>
                ))}
              </List>
            </Card>
          )}
        </Paper>
      </Box>
    );
  }
  
  // STREAM MODE - Show the streaming interface for a specific organization
  return (
    <Box 
      component="main" 
      sx={{ py: 2 }}
      role="main"
      aria-label={`Audio streaming page for ${streamOwner?.displayName || 'organization'}`}
    >
      {/* Add JSON-LD structured data for SEO */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(generateStructuredData())
        }}
      />
      
      {/* iOS Audio Player to handle iOS-specific audio unlocking - now hidden by default */}
      <IosAudioPlayer 
        onAudioUnlocked={handleAudioUnlocked} 
        showControls={false}
        triggerUnlock={triggerAudioUnlock}
      />
      <Paper 
        elevation={3} 
        sx={{ 
          p: 4, 
          borderRadius: 2,
          maxWidth: '800px',
          mx: 'auto'
        }}
      >
        <Typography 
          variant="h1" 
          component="h1" 
          gutterBottom
          sx={{ 
            mb: 3,
            textAlign: 'center',
            fontWeight: 500,
            fontSize: '2rem'
          }}
        >
          {streamOwner ? `Listen to ${streamOwner.displayName}` : 'Listen to Broadcasts'}
        </Typography>
        
        {!invalidCustomerId && streamOwner && (
          <Typography 
            variant="body1" 
            component="p" 
            sx={{ 
              mb: 3, 
              textAlign: 'center',
              maxWidth: '600px',
              mx: 'auto'
            }}
          >
            Listen to {streamOwner.displayName}'s real-time audio with automatic translation. Choose your preferred language and enjoy seamless communication.
          </Typography>
        )}
        
        {/* Display error message for invalid customer ID */}
        {invalidCustomerId && (
          <ErrorMessage 
            title="Invalid Organization ID" 
            message={`We couldn't find an organization with the ID '${customerId}'. Please search for an organization to listen to.`}
            severity="error"
            sx={{ mb: 3 }}
            action={
              <Button 
                color="inherit" 
                size="small" 
                onClick={() => navigate('/stream')}
                sx={{ mt: 1 }}
                aria-label="Return to organization search"
              >
                Go to Search
              </Button>
            }
          />
        )}

        {!invalidCustomerId && (
          <Card elevation={2}>
            <CardContent>
              {/* Connection status - adding semantic heading for better content structure */}
              <Typography variant="h2" component="h2" sx={{ position: 'absolute', width: '1px', height: '1px', overflow: 'hidden' }}>
                Connection Status and Audio Controls
              </Typography>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <StatusIndicator 
                  status={
                    wsState === 'connected' 
                      ? isStreaming ? 'streaming' : 'connected'
                      : wsState === 'reconnecting' ? 'reconnecting' : wsState
                  }
                  label={
                    wsState === 'connected' 
                      ? isStreaming ? 'Streaming' : 'Connected' 
                      : wsState === 'connecting' ? 'Connecting...' 
                      : wsState === 'reconnecting' ? 'Reconnecting...'
                      : 'Disconnected'
                  }
                />
                
                {queueStatus.total > 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ mr: 1 }}>
                      Queue: {queueStatus.pending}/{queueStatus.total}
                    </Typography>
                    
                    {queueStatus.pending > 0 && (
                      <IconButton 
                        size="small" 
                        onClick={clearAudioQueue}
                        title="Clear Queue"
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                )}
              </Box>
              
              {/* Audio visualization */}
              <Box sx={{ 
                width: '100%', 
                display: 'flex', 
                justifyContent: 'center',
                mt: 2,
                mb: 2
              }}>
                <Box sx={{ 
                  width: '100%', 
                  maxWidth: '600px'
                }}>
                  <AudioWaveform isActive={isPlaying} />
                </Box>
              </Box>
              
              {/* Stream control buttons */}
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                gap: 2, // Same spacing on both views
                mt: 3, // Consistent margin across devices
                mb: 2, // Consistent margin across devices
                maxWidth: '600px',
                mx: 'auto'
              }}>
                <Button 
                  variant="contained" 
                  color={isStreaming ? "error" : "primary"}
                  onClick={toggleStreaming}
                  disabled={wsState === 'connecting' || checkingTts}
                  startIcon={isStreaming ? <StopIcon /> : <PlayCircleOutlineIcon />}
                  sx={{ px: 4, py: 1.5, borderRadius: 2 }}
                  fullWidth
                >
                  {isStreaming 
                    ? ttsAvailable
                      ? "Stop Listening"
                      : "Stop Transcribing"
                    : isIOS 
                      ? ttsAvailable 
                        ? "Start Listening (Enables Audio)" 
                        : "Start Transcribing (Enables Audio)"
                      : ttsAvailable 
                        ? "Start Listening" 
                        : "Start Transcribing"
                  }
                </Button>
                
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'center',
                  width: '100%',
                  maxWidth: '600px', 
                  flexWrap: 'wrap', 
                }}>
                  <Box sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' }, 
                    alignItems: { xs: 'center', sm: 'flex-start' }, 
                    justifyContent: { xs: 'flex-start', sm: 'space-between' }, // Space between on desktop
                    width: '100%',
                    gap: 2, // Consistent spacing across devices
                    mt: 0 // No top margin across all devices
                  }}>
                    <Box sx={{ 
                      minWidth: { xs: '100%', sm: '200px' }, 
                      maxWidth: { xs: '100%', sm: '300px' },
                      display: 'flex',
                      flexDirection: 'column', // Always column to ensure warning is below
                      alignItems: { xs: 'center', sm: 'flex-start' }, // Center on mobile, left-align on desktop
                    }}>
                      <LanguageSelector
                        value={targetLanguage}
                        onChange={handleLanguageChange}
                        languages={languageData.languages}
                      />
                      
                      {/* TTS unavailable message - moved outside to be positioned opposite language selector */}
                    </Box>
                    
                    {/* Create a box for the right side that shows either the gender selector or error message */}
                    <Box sx={{ 
                      minWidth: { xs: '100%', sm: 'auto' },
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: { xs: 'center', sm: 'flex-end' }, // Right-aligned on desktop
                      justifyContent: { xs: 'flex-start', sm: 'center' }, // Vertically center in desktop view
                      alignSelf: { xs: 'auto', sm: 'center' }, // Align self center in desktop view
                      mt: { xs: 0, sm: 0 }, // No top margin on mobile since we're using gap on the container
                      height: { xs: 'auto', sm: '100%' } // Full height to enable vertical centering
                    }}>
                      {/* Show TTS unavailable message if TTS is not available */}
                      {!ttsAvailable && !checkingTts && (
                        <Typography
                          variant="caption"
                          color="warning.main"
                          sx={{ 
                            textAlign: { xs: 'center', sm: 'right' }, // Right-aligned text on desktop
                            width: '100%', // Full width to maintain alignment
                            my: { xs: 0, sm: 'auto' } // Auto margin on y-axis for desktop to aid centering
                          }}
                        >
                          Audio playback is unavailable in this language.
                        </Typography>
                      )}
                      
                      {/* Show gender selector if TTS is available */}
                      {ttsAvailable && (
                        <Box sx={{ 
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: { xs: 'center', sm: 'flex-end' }, // Right-aligned on desktop
                          justifyContent: 'center', // Vertically center
                          width: '100%',
                          my: { xs: 0, sm: 'auto' } // Auto margin on y-axis for desktop to aid centering
                        }}>
                          <GenderSelector
                            key={`gender-selector-${targetLanguage}`}
                            value={voiceGender}
                            onChange={handleGenderChange}
                            options={supportedGenders.map(gender => ({
                              value: gender,
                              label: gender === 'MALE' ? 'Male' : 'Female',
                              icon: gender === 'MALE' ? <MaleIcon /> : <FemaleIcon />
                            }))}
                          />
                          
                          {/* Show gender support warning if needed */}
                          {!genderSupported && (
                            <Typography
                              variant="caption"
                              color="warning.main"
                              sx={{ 
                                mt: 0.5, 
                                textAlign: { xs: 'center', sm: 'right' },
                                width: '100%'
                              }}
                            >
                              This voice gender may not be available for all languages.
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        )}
        
        {/* Only show transcriptions if we have a valid org */}
        {!invalidCustomerId && (
          <Card elevation={2} sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Transcriptions
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Box 
                ref={transcriptionBoxRef}
                onScroll={handleTranscriptionScroll}
                sx={{ 
                  height: '300px', 
                  overflowY: 'auto',
                  p: 2,
                  borderRadius: 1,
                  bgcolor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider',
                  fontFamily: 'system-ui',
                  fontSize: '1rem',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word'
                }}
              >
                {fullTranscript.trim() === '' ? (
                  <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
                    No transcriptions yet. Start listening to receive transcriptions.
                  </Typography>
                ) : (
                  <Typography variant="body1">{displayedTranscript}</Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        )}
        

      </Paper>
    </Box>
  );
};

export default StreamingPage;
